import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { faPaperPlaneTop } from '@fortawesome/pro-solid-svg-icons';
import { faReply, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { splitTextByChannelMention } from '../lib/channelMention';

export type AddReplyHandle = { focus: () => void };

type ReplyTarget = { name: string; showMention: boolean };

type Props = {
  onSubmit: (text: string) => void;
  autoFocus?: boolean;
  mentions?: string[];
  replyingTo?: ReplyTarget | null;
  onCancelReply?: () => void;
};

export const AddReply = forwardRef<AddReplyHandle, Props>(
  (
    { onSubmit, autoFocus = false, mentions, replyingTo, onCancelReply },
    ref,
  ) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    // Prefill the mention into the text so it flows (and wraps) inline with the
    // reply, instead of sitting in a separate column that indents wrapped lines.
    const replyName = replyingTo?.showMention ? replyingTo.name : '';
    const [text, setText] = useState(replyName ? `@${replyName} ` : '');
    const [appliedReplyName, setAppliedReplyName] = useState(replyName);
    if (replyName !== appliedReplyName) {
      setAppliedReplyName(replyName);
      setText(replyName ? `@${replyName} ` : '');
    }

    const canSubmit = text.trim().length > 0;
    const parts = splitTextByChannelMention(text, mentions);

    const focusInput = useCallback(() => {
      const input = inputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
      input.setSelectionRange(input.value.length, input.value.length);
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          rootRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          focusInput();
        },
      }),
      [focusInput],
    );

    useEffect(() => {
      if (autoFocus) focusInput();
    }, [autoFocus, focusInput]);

    useEffect(() => {
      const input = inputRef.current;
      if (!input) return;
      input.style.height = 'auto';
      input.style.height = `${input.scrollHeight}px`;
    }, [text]);

    const syncHighlightScroll = () => {
      if (highlightRef.current && inputRef.current) {
        highlightRef.current.scrollTop = inputRef.current.scrollTop;
      }
    };

    const submit = () => {
      if (!canSubmit) return;
      onSubmit(text.trim());
      setText('');
    };

    const field = (
      <div className="flex flex-col gap-4 rounded-[10px] border border-[#ECEDF2] bg-white p-4 focus-within:border-[#5F1ECC]">
        <div className="relative min-h-[48px] w-full">
          <div
            ref={highlightRef}
            aria-hidden
            className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words text-[16px] leading-6 text-[#131313]"
          >
            {parts.map((part, index) =>
              part.isMention ? (
                <span
                  key={index}
                  data-mention="true"
                  className="rounded-[4px] bg-[#EDE5FC] text-[#5F1ECC]"
                >
                  {part.text}
                </span>
              ) : (
                <span key={index}>{part.text}</span>
              ),
            )}
          </div>
          <textarea
            ref={inputRef}
            value={text}
            placeholder={replyingTo ? 'Write reply' : 'Write comment'}
            rows={1}
            onChange={(event) => setText(event.target.value)}
            onScroll={syncHighlightScroll}
            className="relative block min-h-[48px] w-full resize-none overflow-hidden bg-transparent text-[16px] leading-6 text-transparent caret-[#131313] outline-none placeholder:text-[#646464]"
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            aria-label="Send the reply"
            disabled={!canSubmit}
            onClick={submit}
            className="flex items-center gap-2 rounded-[10px] bg-[#EDE5FC] px-4 py-2 text-base font-medium text-[#350584] disabled:bg-[#EEEEEE] disabled:text-[#9E9E9E]"
          >
            <FontAwesomeIcon icon={faPaperPlaneTop} className="text-base" />
            <span>Send</span>
          </button>
        </div>
      </div>
    );

    if (!replyingTo) return <div ref={rootRef}>{field}</div>;

    return (
      <div
        ref={rootRef}
        className="flex flex-col overflow-clip rounded-[10px] bg-[#F8F8F8]"
      >
        <div className="flex h-8 items-center justify-between px-[13px] py-1 text-[12px]">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faReply} className="text-[#8A8E99]" />
            <span className="leading-4 text-[#8A8E99]">Replying to</span>
            <span className="font-medium leading-4 text-[#5F1ECC]">
              {replyingTo.name}
            </span>
          </div>
          <button
            type="button"
            aria-label="Cancel replying"
            onClick={onCancelReply}
            className="text-[#8A8E99]"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        {field}
      </div>
    );
  },
);

AddReply.displayName = 'AddReply';
